import { useCallback, useMemo } from 'react';

import { mapCoachNotification } from '@/features/coach/notifications/constants';
import type { CoachNotification } from '@/features/coach/notifications/types';
import {
  useCoachNotificationsQuery,
  useDeleteCoachNotificationMutation,
  useMarkAllCoachNotificationsReadMutation,
  useMarkCoachNotificationReadMutation,
} from '@/features/coach/queries/notifications';

type UseCoachNotificationsOptions = {
  /** Called after the tap has been marked read, with the resolved destination (or none). */
  onNavigate?: (item: CoachNotification) => void;
};

/**
 * View-model for the Coach Notification Center: flattens the paginated cache into a
 * newest-first list (BR-01) and exposes the read / delete / refresh handlers.
 */
export function useCoachNotifications(_options: UseCoachNotificationsOptions = {}) {
  // const { onNavigate } = options;
  const { data, refetch, fetchNextPage, hasNextPage, isRefetching, isFetchingNextPage, isLoading } =
    useCoachNotificationsQuery();
  const { mutate: markAsRead } = useMarkCoachNotificationReadMutation();
  const { mutate: markAllAsRead } = useMarkAllCoachNotificationsReadMutation();
  const { mutate: deleteNotification } = useDeleteCoachNotificationMutation();

  const notifications = useMemo(() => {
    // Pages can overlap when items are deleted mid-pagination — de-dupe by id before mapping.
    const unique = new Map(
      (data?.pages ?? []).flatMap(page => page.notifications).map(item => [item.id, item]),
    );

    return Array.from(unique.values())
      .map(mapCoachNotification)
      .sort((a, b) => b.receivedAt - a.receivedAt);
  }, [data?.pages]);

  const unreadCount = data?.pages[0]?.unreadCount ?? 0;

  const handleNotificationPress = useCallback(
    (item: CoachNotification) => {
      if (!item.isRead) markAsRead(item.id);
      // onNavigate?.(item);
    },
    [markAsRead],
  );

  const handleMarkAllRead = useCallback(() => markAllAsRead(), [markAllAsRead]);
  const handleDelete = useCallback((id: string) => deleteNotification(id), [deleteNotification]);
  const handleRefresh = useCallback(() => refetch(), [refetch]);
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isReady: Boolean(data),
    isRefreshing: isRefetching && !isFetchingNextPage,
    isFetchingNextPage,
    handleNotificationPress,
    handleMarkAllRead,
    handleDelete,
    handleRefresh,
    handleLoadMore,
  };
}

/** Unread count from the notifications cache — drives the bottom-nav badge. */
export function useCoachNotificationUnreadCount() {
  const { data } = useCoachNotificationsQuery();
  return data?.pages[0]?.unreadCount ?? 0;
}

/** True when the coach has unread notifications. */
export function useCoachNotificationBadge() {
  return useCoachNotificationUnreadCount() > 0;
}
